import { createFileRoute, redirect, Outlet, useNavigate, useMatches } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useStore } from '@tanstack/react-store'
import { useAuth } from '../hooks/useAuth'
import { useCurrentUser, getMeQueryOptions } from '../stores/user.store'
import { usePermissions } from '../hooks/use-permissions'
import {
  organizationStore,
  listMyOrganizationsQueryOptions,
  listOrganizationUsersQueryOptions,
  getOrganizationQueryOptions,
  seedSelectedOrganizationFromStorage,
  reconcileSelectedOrganization,
  adoptSupportOrganizationFromUrl,
} from '../stores/organization.store'
import { getOrganizationBillingStateQueryOptions } from '../stores/billing.store'
import { authReady } from '../stores/auth.store'
import { AppShell } from '../components/layout/app-shell'
import { BreadcrumbProvider, useBreadcrumbs } from '../contexts/breadcrumb-context'
import { AIAssistantProvider } from '../contexts/ai-assistant-context'
import { AIAssistantDock } from '../components/ai/ai-assistant-dock'
import { getAiOrbConfigQueryOptions } from '../stores/ai-orb.store'
import { ContentHeader } from '../components/layout/content-header'
import { isDemoModeActive } from '../lib/demo-mode'
import { AuthenticatedNotFoundPage } from '../components/shared/not-found'
import { Button } from '../components/ui/button'
import { INACTIVE_ACCOUNT_MESSAGE, isInactiveAccountError } from '../lib/inactive-account'
import { resolveBillingGateRedirect } from '../lib/billing-gate'
import { loadShellPrerequisites } from '../lib/app-shell-prerequisites'
import { eventStaffRedirectTarget } from '../lib/event-staff-gate'
import { BillingStateBanner } from '../components/subscription/BillingStateBanner'
import { MailboxReconnectBanner } from '../components/settings/email/mailbox-reconnect-banner'
import { SupportSessionBanner } from '../components/support/support-session-banner'

export const Route = createFileRoute('/_app')({
  component: AppLayout,
  notFoundComponent: AuthenticatedNotFoundPage,
  pendingComponent: () => <div className="flex min-h-svh items-center justify-center">Loading...</div>,
  // Parent beforeLoad hooks run serially before matched route loaders. Keeping
  // initialization here guarantees child loaders see auth, GetMe, and the
  // reconciled organization instead of racing this work on a cold load.
  beforeLoad: async ({ context, location }) => {
    const isDemo = isDemoModeActive()

    // A superadmin support link (?supportOrg=) replaces the persisted org
    // choice. Done before the session check so it survives a sign-in redirect.
    if (!isDemo) adoptSupportOrganizationFromUrl()

    // Wait for the initial supabase session check (skip in demo mode, which
    // bypasses auth entirely). Don't fire authenticated RPCs beforehand.
    if (!isDemo) {
      await authReady
      if (!context.auth.state.session) return
    }

    // Optimistically select the org persisted in localStorage (read
    // synchronously at module load) so every org-scoped query joins this first
    // wave instead of waiting a tier on ListMyOrganizations. Reconciled below
    // if the stored id is stale.
    const seededOrganizationId = seedSelectedOrganizationFromStorage()
    if (seededOrganizationId) {
      // Observe the rejection: a stale seed gets PermissionDenied here, and the
      // membership check below is what actually reconciles it.
      context.queryClient
        .ensureQueryData(getOrganizationQueryOptions(seededOrganizationId))
        .catch(() => {})
      // Most of the sidebar is permission-gated, and usePermissions sources the
      // effective set from ListOrganizationUsers. Join the first wave here so
      // the awaited resolve below is already in flight on the common path.
      context.queryClient
        .ensureQueryData(listOrganizationUsersQueryOptions(seededOrganizationId))
        .catch(() => {})
      // The billing gate below needs only the organization id too, so it joins
      // the first wave rather than waiting on ListMyOrganizations.
      if (!isDemo) {
        context.queryClient
          .ensureQueryData(getOrganizationBillingStateQueryOptions(seededOrganizationId))
          .catch(() => {})
      }
    }

    // Global AI orb config: fire-and-forget so a slow/failed fetch never
    // blocks the shell — AIOrb falls back to its built-in defaults while
    // this resolves.
    context.queryClient.ensureQueryData(getAiOrbConfigQueryOptions()).catch(() => {})

    // GetMe and ListMyOrganizations don't depend on each other — fetch them
    // in parallel instead of serializing on component mount order.
    const [, orgsResponse] = await Promise.all([
      context.queryClient.ensureQueryData(getMeQueryOptions()),
      context.queryClient.ensureQueryData(listMyOrganizationsQueryOptions()),
    ])

    // Select the org as soon as the list resolves, before the sidebar mounts,
    // so GetOrganization can start immediately instead of waiting on the
    // TeamSwitcher's effect.
    reconcileSelectedOrganization(orgsResponse.organizations, seededOrganizationId, context.queryClient)

    const selectedOrganizationId = organizationStore.state.selectedOrganizationId
    if (selectedOrganizationId && selectedOrganizationId !== seededOrganizationId) {
      void context.queryClient.ensureQueryData(getOrganizationQueryOptions(selectedOrganizationId))
    }

    // Resolve the permission set and the billing access state before the shell
    // mounts, concurrently. Without the permission set the sidebar's first
    // paint has an empty effective-permission set, so every gated nav item pops
    // in a tick later. On the common path (valid seed) both await requests
    // started above. Failures are swallowed: the sidebar degrades to its
    // ungated items, and billing infrastructure errors fail open, matching the
    // server interceptor.
    if (selectedOrganizationId) {
      const billingResponse = await loadShellPrerequisites({
        permissions: () =>
          context.queryClient.ensureQueryData(listOrganizationUsersQueryOptions(selectedOrganizationId)),
        billing: isDemo
          ? null
          : () => context.queryClient.ensureQueryData(getOrganizationBillingStateQueryOptions(selectedOrganizationId)),
      })

      // Route from the backend-derived access reason. Incomplete onboarding is
      // contained outside the application shell; suspension and paid expiry
      // keep their existing recovery-only destinations.
      if (!isDemo) {
        const target = resolveBillingGateRedirect(location.pathname, billingResponse?.billingState ?? null)
        if (target) throw redirect({ href: target })
      }
    }
  },
})

function AppLayout() {
  const navigate = useNavigate()
  const { session, isLoading: authLoading, signOut } = useAuth()
  const {
    isLoading: userLoading,
    profileComplete,
    hasOrganization,
    error: userError,
  } = useCurrentUser()
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const { isEventStaff } = usePermissions(selectedOrganizationId || '')
  const matches = useMatches()

  // In demo mode, bypass authentication check
  const isDemo = isDemoModeActive()

  useEffect(() => {
    // If not loading and no session, redirect to login (skip in demo mode)
    if (!isDemo && !authLoading && !session) {
      navigate({ to: '/login' })
    }
  }, [session, authLoading, navigate, isDemo])

  useEffect(() => {
    if (!isEventStaff) return
    const currentPath = matches[matches.length - 1]?.pathname
    const target = eventStaffRedirectTarget(currentPath)
    if (target) {
      navigate({ to: target, replace: true })
    }
  }, [isEventStaff, matches, navigate])

  // Redirect to onboarding based on profile/organization state
  useEffect(() => {
    if (isDemo || userLoading) return

    if (profileComplete === false) {
      navigate({ to: '/onboarding/user' })
    } else if (hasOrganization === false) {
      navigate({ to: '/onboarding/organization' })
    }
  }, [isDemo, userLoading, profileComplete, hasOrganization, navigate])

  // In demo mode, skip auth check
  if (isDemo) {
    // Continue to render the app with demo data
  } else {
    if (authLoading) {
      return <div className="flex min-h-svh items-center justify-center">Loading...</div>
    }

    if (!session) {
      return null // Will redirect via useEffect
    }

    // Wait for user check to complete before rendering
    if (userLoading) {
      return <div className="flex min-h-svh items-center justify-center">Loading...</div>
    }

    if (isInactiveAccountError(userError)) {
      return <InactiveAccountScreen onSignOut={signOut} />
    }

    // If user needs onboarding, wait for redirect (handled by useEffect above)
    if (profileComplete === false || hasOrganization === false) {
      return <div className="flex min-h-svh items-center justify-center">Redirecting to onboarding...</div>
    }
  }

  return (
    <BreadcrumbProvider>
      <AIAssistantProvider>
        <AppShell>
          <AppLayoutContent />
        </AppShell>
      </AIAssistantProvider>
    </BreadcrumbProvider>
  )
}

function InactiveAccountScreen({ onSignOut }: { onSignOut: () => Promise<void> }) {
  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="w-full max-w-md rounded-lg border bg-background p-8 text-center shadow-sm">
        <h1 className="text-2xl font-bold">Account inactive</h1>
        <p className="mt-3 text-sm text-muted-foreground">{INACTIVE_ACCOUNT_MESSAGE}</p>
        <Button className="mt-6" onClick={() => void onSignOut()}>
          Sign out
        </Button>
      </div>
    </div>
  )
}

export function AppLayoutContent() {
  const { breadcrumbs } = useBreadcrumbs()
  const matches = useMatches()

  // Check if we're on the chat-style route (needs its own scroll management)
  const isOnChatRoute = matches.some(match => match.pathname === '/chat')
  const isOnStartTrialRoute = matches.some(match => match.pathname === '/start-trial')
  const isOnCompanyRoute = matches.some(match => match.pathname === '/company')
  const isOnEmailSettingsRoute = matches.some(match => match.pathname === '/email')

  return (
    <>
      <SupportSessionBanner className="shrink-0 rounded-none border-x-0 border-t-0" />
      <ContentHeader breadcrumbs={breadcrumbs} />
      {!isOnStartTrialRoute && (
        <div className="shrink-0 px-4 pt-3">
          <BillingStateBanner
            showAction={!isOnCompanyRoute}
            className="rounded-lg"
          />
        </div>
      )}
      <MailboxReconnectBanner
        showSettingsLink={!isOnEmailSettingsRoute}
        className="mx-4 mt-3 w-auto shrink-0 rounded-lg"
      />
      <div className="flex-1 overflow-hidden flex">
        {/* Chat page manages its own scrolling, other pages scroll normally */}
        <div className={isOnChatRoute ? "flex-1 flex flex-col overflow-hidden" : "flex-1 overflow-y-scroll"}>
          <Outlet />
        </div>
      </div>
      {/* The compact assistant shares its provider/session with the full chat route. */}
      {!isOnChatRoute && <AIAssistantDock />}
    </>
  )
}
